import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", description: "", image_file_id: null, published: true, order: 0 };

export default function AdminTestimonials() {
  useSEO("Admin · Testimonials");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/testimonials").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/testimonials", { ...form, order: Number(form.order) || 0 }); toast.success("Added"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="Testimonials" subtitle="Upload testimonial screenshots & quotes"
        action={<Button onClick={() => { setForm(empty); setOpen(true); }} className="gradient-brand text-brand-navy font-semibold" data-testid="new-testimonial-btn"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 ? <p className="text-slate-500 col-span-full">No testimonials yet.</p>
          : rows.map((tm) => (
            <div key={tm.id} className="card-surface rounded-2xl overflow-hidden" data-testid={`admin-testimonial-${tm.id}`}>
              {tm.image_file_id && <img src={fileUrl(tm.image_file_id)} alt={tm.name} className="w-full object-cover" />}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <p className="font-display font-semibold text-white">{tm.name || "Anonymous"}</p>
                  <button onClick={() => del(tm.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-testimonial-${tm.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                {tm.description && <p className="mt-1 text-sm text-slate-400">{tm.description}</p>}
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">Add Testimonial</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <ImageUpload value={form.image_file_id} onChange={(v) => setForm({ ...form, image_file_id: v })} label="Screenshot / Image" />
            <div><Label className="text-slate-300">Name (optional)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="testimonial-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
